import React, { FunctionComponent } from 'react'
import { FormattedDateTimeRange, FormattedMessage, useIntl } from 'react-intl'

import { differenceInHours, parseISO } from 'date-fns'
import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { SelectField } from '../../atoms/formElements/SelectField'
import { InfoIcon } from '../../atoms/icons/InfoIcon'
import { LoadingIndicator } from '../../atoms/icons/LoadingIndicator'
import { ScheduleIcon } from '../../atoms/icons/ScheduleIcon'
import { Title } from '../../atoms/title/Title'
import { TOOLTIP_TRIGGER_ICON } from '../../atoms/tooltip/Tooltip'
import { BodyTextSize, TitleSize } from '../../styles/typeStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export type ProviderCalendarEditSessionModalityContentProps = {
  clientFirstName: string
  clientLastName: string
  startDate: string
  endDate: string
  timeZone: string
  upcomingAppointmentIds?: number[]
  selectedModality: string
  setSelectedModality: (value: string) => void
  isLoadingClientBookingDetails: boolean
  modalityOptions: { label: string; value: string }[]
}

const LoadingContainer = styled.View({
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '250px',
})

const SessionInfoCard = styled.View(({ theme }) => ({
  backgroundColor: theme.colors.backgroundSection,
  borderRadius: theme.spacing['8px'],
  padding: theme.spacing['16px'],
  gap: theme.spacing['8px'],
}))

const SessionInfoRow = styled.View(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing['8px'],
}))

const ModalBody = styled.View(({ theme }) => ({
  gap: theme.spacing['16px'],
  width: '100%',
}))

const InfoIconContainer = styled.View(({ theme }) => ({
  minWidth: 24,
  paddingTop: theme.spacing['2px'],
  paddingLeft: theme.spacing['4px'],
}))

export const ProviderCalendarEditSessionModalityContent: FunctionComponent<
  ProviderCalendarEditSessionModalityContentProps
> = ({
  clientFirstName,
  clientLastName,
  startDate,
  endDate,
  timeZone,
  upcomingAppointmentIds,
  selectedModality,
  setSelectedModality,
  isLoadingClientBookingDetails,
  modalityOptions,
}) => {
  const { colors } = useTheme() as ThemeType
  const { formatMessage } = useIntl()
  const isUpcomingSessionsLoaded = upcomingAppointmentIds !== undefined
  const hasUpcomingSessions = upcomingAppointmentIds != null && upcomingAppointmentIds.length > 1
  const isWithin72Hours = differenceInHours(parseISO(startDate), new Date()) < 72

  const emailNotificationText = (
    <FormattedMessage
      defaultMessage="If it's less than 72 hours before the session time we'll email this client notifying them of the change."
      description='Info banner shown when editing modality'
    />
  )

  if (isLoadingClientBookingDetails) {
    return (
      <LoadingContainer>
        <LoadingIndicator topPadding={0} />
      </LoadingContainer>
    )
  }

  return (
    <ModalBody testID={tID('ProviderCalendarEditSessionModalityContent-body')}>
      <SessionInfoCard testID={tID('ProviderCalendarEditSessionModalityContent-sessionInfoCard')}>
        <Title
          size={TitleSize.XSMALL}
          text={
            <FormattedMessage
              defaultMessage='{firstName} {lastName}'
              description='Client full name in edit modality modal'
              values={{ firstName: clientFirstName, lastName: clientLastName }}
            />
          }
        />
        <SessionInfoRow testID={tID('ProviderCalendarEditSessionModalityContent-sessionInfoRow')}>
          <ScheduleIcon fillColor={colors.iconDefault} size={24} />
          <BodyText
            size={BodyTextSize.SMALL}
            testID={tID('ProviderCalendarEditSessionModalityContent-sessionInfoTime')}
            text={
              <FormattedDateTimeRange
                from={parseISO(startDate)}
                to={parseISO(endDate)}
                weekday='short'
                month='long'
                day='numeric'
                hour='numeric'
                minute='numeric'
                timeZoneName='short'
                timeZone={timeZone}
              />
            }
          />
        </SessionInfoRow>
        {isUpcomingSessionsLoaded && !hasUpcomingSessions && isWithin72Hours && (
          <SessionInfoRow
            testID={tID('ProviderCalendarEditSessionModalityContent-imminentSessionNotificationContainer')}
          >
            <InfoIconContainer>
              <InfoIcon fillColor={colors.iconDefault} size={16} />
            </InfoIconContainer>
            <BodyText
              size={BodyTextSize.SMALL}
              testID={tID('ProviderCalendarEditSessionModalityContent-imminentSessionNotificationText')}
              text={emailNotificationText}
            />
          </SessionInfoRow>
        )}
      </SessionInfoCard>
      <SelectField
        label={formatMessage({ defaultMessage: 'Modality', description: 'Label for modality selector in edit modal' })}
        value={selectedModality}
        onChange={setSelectedModality}
        options={modalityOptions}
        name='meetingFormat'
        disablePlaceholder
        toolTipContent={formatMessage({
          defaultMessage: 'To change your default setting go to Settings > Video session preference',
          description: 'Tooltip explaining how to change default modality setting',
        })}
        toolTipTriggerConfig={{ icon: TOOLTIP_TRIGGER_ICON.INFO }}
        toolTipHoverEnabled
      />
    </ModalBody>
  )
}
